'use client';

/**
 * PPGMonitor — Browser-based Photoplethysmography Module
 *
 * Measures heart rate and HRV from a fingertip placed over the device camera.
 * Pipeline: Camera → Frame Capture → Detrend → Bandpass → Peak Detection → BPM/RMSSD
 *
 * Privacy: No video frames stored or transmitted. Only derived metrics are persisted.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { CameraHandler, FrameSample } from './ppg/cameraHandler';
import { SignalProcessor, PPGMetrics } from './ppg/signalProcessor';

// ─── Constants ────────────────────────────────────────────────────────────────

const SAMPLE_RATE = 30;          // Target FPS
const WINDOW_SECONDS = 15;       // Rolling analysis window
const SESSION_DURATION = 45;     // Default session length (seconds)
const MIN_BPM = 40;
const MAX_BPM = 200;
const WAVEFORM_POINTS = 150;

// ─── Types ────────────────────────────────────────────────────────────────────

type SessionState = 'idle' | 'initialising' | 'measuring' | 'complete' | 'error';

interface GuidanceMessage {
  text: string;
  type: 'info' | 'warning' | 'success' | 'error';
}

// ─── Waveform SVG ─────────────────────────────────────────────────────────────

function PulseWaveform({ data, quality }: { data: number[]; quality: number }) {
  if (data.length < 2) {
    return (
      <div className="flex items-center justify-center h-full">
        <span className="text-zinc-600 text-xs">Waiting for signal…</span>
      </div>
    );
  }

  const width = 600;
  const height = 80;
  const padding = 4;
  const usableWidth = width - padding * 2;
  const usableHeight = height - padding * 2;

  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;

  const points = data.map((v, i) => {
    const x = padding + (i / (data.length - 1)) * usableWidth;
    const y = padding + (1 - (v - min) / range) * usableHeight;
    return `${x},${y}`;
  });

  const strokeColor = quality > 0.7 ? '#22c55e' : quality > 0.4 ? '#f97316' : '#ef4444';

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      className="w-full h-full"
      preserveAspectRatio="none"
    >
      <defs>
        <linearGradient id="ppgGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={strokeColor} stopOpacity="0.3" />
          <stop offset="100%" stopColor={strokeColor} stopOpacity="0" />
        </linearGradient>
      </defs>
      {/* Fill area */}
      <path
        d={`M ${points[0]} ${points.slice(1).map((p) => `L ${p}`).join(' ')} L ${padding + usableWidth},${padding + usableHeight} L ${padding},${padding + usableHeight} Z`}
        fill="url(#ppgGrad)"
      />
      {/* Waveform line */}
      <polyline
        points={points.join(' ')}
        fill="none"
        stroke={strokeColor}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// ─── Quality Indicator ────────────────────────────────────────────────────────

function QualityBar({ quality }: { quality: number }) {
  const pct = Math.round(quality * 100);
  const color = quality > 0.7 ? 'bg-emerald-500' : quality > 0.4 ? 'bg-orange-500' : 'bg-red-500';
  const label = quality > 0.7 ? 'Good' : quality > 0.4 ? 'Fair' : 'Poor';

  return (
    <div className="flex items-center gap-2">
      <span className="text-[11px] text-zinc-500 w-10 shrink-0">Quality</span>
      <div className="flex-1 h-1.5 bg-zinc-800 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-500 ${color}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className={`text-[11px] font-semibold w-8 text-right ${quality > 0.7 ? 'text-emerald-400' : quality > 0.4 ? 'text-orange-400' : 'text-red-400'}`}>
        {label}
      </span>
    </div>
  );
}

// ─── Guidance Banner ──────────────────────────────────────────────────────────

function GuidanceBanner({ message }: { message: GuidanceMessage }) {
  const styles: Record<GuidanceMessage['type'], string> = {
    info: 'bg-sky-950/40 border-sky-800/40 text-sky-300',
    warning: 'bg-amber-950/40 border-amber-800/40 text-amber-300',
    success: 'bg-emerald-950/40 border-emerald-800/40 text-emerald-300',
    error: 'bg-red-950/40 border-red-800/40 text-red-300',
  };

  return (
    <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-[12px] font-medium ${styles[message.type]}`}>
      <span className="text-base leading-none">
        {message.type === 'success' ? '✓' : message.type === 'warning' ? '⚠' : message.type === 'error' ? '✕' : 'ℹ'}
      </span>
      {message.text}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

interface PPGMonitorProps {
  eegSessionTag?: string;
  onMetricsUpdate?: (metrics: PPGMetrics) => void;
}

export default function PPGMonitor({ eegSessionTag, onMetricsUpdate }: PPGMonitorProps) {
  const [sessionState, setSessionState] = useState<SessionState>('idle');
  const [metrics, setMetrics] = useState<PPGMetrics | null>(null);
  const [waveform, setWaveform] = useState<number[]>([]);
  const [torchAvailable, setTorchAvailable] = useState<boolean | null>(null);
  const [guidance, setGuidance] = useState<GuidanceMessage>({ text: 'Place your fingertip gently over the rear camera and flash.', type: 'info' });
  const [elapsed, setElapsed] = useState(0);
  const [sessionDuration] = useState(SESSION_DURATION);
  const [saving, setSaving] = useState(false);
  const [savedId, setSavedId] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const cameraRef = useRef<CameraHandler | null>(null);
  const processorRef = useRef<SignalProcessor | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startTimeRef = useRef<number>(0);
  const frameCountRef = useRef(0);
  const lastRedRef = useRef(128);

  // ── Guidance logic ──────────────────────────────────────────────────────────
  const updateGuidance = useCallback((quality: number, redMean: number) => {
    if (redMean < 30) {
      setGuidance({ text: 'No finger detected — place fingertip firmly over camera.', type: 'warning' });
    } else if (redMean > 240) {
      setGuidance({ text: 'Too much light — reduce pressure or cover flash completely.', type: 'warning' });
    } else if (quality < 0.3) {
      setGuidance({ text: 'Hold still and press finger gently over camera.', type: 'warning' });
    } else if (quality < 0.6) {
      setGuidance({ text: 'Good — keep finger still for a cleaner signal.', type: 'info' });
    } else {
      setGuidance({ text: 'Excellent signal — measuring heart rate…', type: 'success' });
    }
  }, []);

  // ── Frame handler ───────────────────────────────────────────────────────────
  const handleFrame = useCallback((sample: FrameSample) => {
    frameCountRef.current++;
    lastRedRef.current = sample.redMean;

    const result = processorRef.current?.process(sample.redMean) ?? null;

    if (result) {
      setMetrics(result);
      onMetricsUpdate?.(result);
      updateGuidance(result.signalQuality, sample.redMean);
    }

    // Update waveform every 3 frames to reduce render load
    if (frameCountRef.current % 3 === 0) {
      const wave = processorRef.current?.getWaveform(WAVEFORM_POINTS) ?? [];
      setWaveform(wave);
    }
  }, [onMetricsUpdate, updateGuidance]);

  // ── Start session ───────────────────────────────────────────────────────────
  const startSession = useCallback(async () => {
    setSessionState('initialising');
    setErrorMsg(null);
    setSavedId(null);
    setMetrics(null);
    setWaveform([]);
    setElapsed(0);
    frameCountRef.current = 0;

    processorRef.current = new SignalProcessor({
      sampleRate: SAMPLE_RATE,
      windowSeconds: WINDOW_SECONDS,
      minBPM: MIN_BPM,
      maxBPM: MAX_BPM,
    });

    cameraRef.current = new CameraHandler(
      { targetFPS: SAMPLE_RATE, torchEnabled: true },
      {
        onFrame: handleFrame,
        onError: (err) => {
          setErrorMsg(err);
          setSessionState('error');
          setGuidance({ text: `Camera error: ${err}`, type: 'error' });
        },
        onTorchStatus: (available) => setTorchAvailable(available),
      }
    );

    await cameraRef.current.start();
    startTimeRef.current = Date.now();
    setSessionState('measuring');
    setGuidance({ text: 'Place your fingertip gently over the rear camera and flash.', type: 'info' });

    // Session countdown timer
    timerRef.current = setInterval(() => {
      const secs = Math.floor((Date.now() - startTimeRef.current) / 1000);
      setElapsed(secs);
      if (secs >= sessionDuration) {
        stopSession(secs);
      }
    }, 500);
  }, [handleFrame, sessionDuration]);

  // ── Stop session ────────────────────────────────────────────────────────────
  const stopSession = useCallback(async (durationOverride?: number) => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    cameraRef.current?.stop();
    cameraRef.current = null;

    const duration = durationOverride ?? Math.floor((Date.now() - startTimeRef.current) / 1000);
    setSessionState('complete');
    setGuidance({ text: 'Session complete. Results saved below.', type: 'success' });

    // Save to backend API
    const currentMetrics = metrics;
    if (currentMetrics && currentMetrics.heartRate > 0) {
      setSaving(true);
      try {
        const res = await fetch('/api/ppg/session', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            session: {
              heartRate: currentMetrics.heartRate,
              rmssd: currentMetrics.rmssd,
              ibiArray: currentMetrics.ibiArray,
              signalQuality: currentMetrics.signalQuality,
              durationSeconds: duration,
              deviceInfo: 'camera_ppg',
            },
          }),
        });
        if (res.ok) {
          const { data: ppgData } = await res.json();
          if (ppgData?.sessionId) setSavedId(ppgData.sessionId);
        }
      } catch {
        // Non-critical — metrics still displayed
      } finally {
        setSaving(false);
      }
    }
  }, [metrics]);

  // ── Cleanup on unmount ──────────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      cameraRef.current?.stop();
    };
  }, []);

  const progress = Math.min(1, elapsed / sessionDuration);
  const remaining = Math.max(0, sessionDuration - elapsed);

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-4">
      {/* Header row */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <span className="text-[13px] font-bold text-white">PPG Heart Rate Monitor</span>
          {torchAvailable === true && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-950/40 border border-amber-800/40 text-amber-400 font-medium">
              🔦 Torch ON
            </span>
          )}
          {torchAvailable === false && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-800/40 border border-zinc-700/40 text-zinc-500 font-medium">
              No torch
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {sessionState === 'idle' || sessionState === 'complete' || sessionState === 'error' ? (
            <button
              onClick={startSession}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-semibold bg-[#F97316]/10 border border-[#F97316]/30 hover:bg-[#F97316]/20 hover:border-[#F97316]/60 text-[#F97316] transition-all duration-150 active:scale-95"
            >
              <span className="w-2 h-2 rounded-full bg-[#F97316] animate-pulse" />
              {sessionState === 'complete' ? 'New Session' : 'Start Measurement'}
            </button>
          ) : sessionState === 'measuring' ? (
            <button
              onClick={() => stopSession()}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-semibold bg-red-950/30 border border-red-800/40 hover:bg-red-900/40 text-red-400 transition-all duration-150 active:scale-95"
            >
              ■ Stop
            </button>
          ) : (
            <span className="text-[12px] text-zinc-500 animate-pulse">Initialising camera…</span>
          )}
        </div>
      </div>

      {/* Guidance banner */}
      <GuidanceBanner message={guidance} />

      {/* Error */}
      {errorMsg && (
        <div className="text-[12px] text-red-400 bg-red-950/20 border border-red-800/30 rounded-lg px-3 py-2">
          {errorMsg}
        </div>
      )}

      {/* Session progress bar */}
      {sessionState === 'measuring' && (
        <div className="space-y-1">
          <div className="flex justify-between text-[11px] text-zinc-500">
            <span>Session progress</span>
            <span>{remaining}s remaining</span>
          </div>
          <div className="h-1 bg-zinc-800 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-[#F97316] to-rose-500 rounded-full transition-all duration-500"
              style={{ width: `${progress * 100}%` }}
            />
          </div>
        </div>
      )}

      {/* Live waveform */}
      <div className="bg-[#04080F] border border-[#1E3A5F]/50 rounded-xl overflow-hidden">
        <div className="px-3 pt-2 pb-1 flex items-center justify-between">
          <span className="text-[11px] text-zinc-500 font-medium uppercase tracking-wider">Live Pulse Waveform</span>
          {sessionState === 'measuring' && (
            <span className="flex items-center gap-1 text-[11px] text-emerald-400">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              Live
            </span>
          )}
        </div>
        <div className="h-20 px-2 pb-2">
          <PulseWaveform data={waveform} quality={metrics?.signalQuality ?? 0} />
        </div>
      </div>

      {/* Metrics grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {/* BPM */}
        <div className="bg-[#04080F] border border-[#1E3A5F]/50 rounded-xl p-3 text-center">
          <div className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Heart Rate</div>
          <div className={`text-3xl font-bold tabular-nums transition-all duration-300 ${metrics?.heartRate ? 'text-[#F97316]' : 'text-zinc-700'}`}>
            {metrics?.heartRate ?? '--'}
          </div>
          <div className="text-[10px] text-zinc-600 mt-0.5">BPM</div>
        </div>

        {/* RMSSD */}
        <div className="bg-[#04080F] border border-[#1E3A5F]/50 rounded-xl p-3 text-center">
          <div className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1">RMSSD</div>
          <div className={`text-3xl font-bold tabular-nums transition-all duration-300 ${metrics?.rmssd ? 'text-sky-400' : 'text-zinc-700'}`}>
            {metrics?.rmssd ?? '--'}
          </div>
          <div className="text-[10px] text-zinc-600 mt-0.5">ms</div>
        </div>

        {/* IBI count */}
        <div className="bg-[#04080F] border border-[#1E3A5F]/50 rounded-xl p-3 text-center">
          <div className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1">IBI Count</div>
          <div className={`text-3xl font-bold tabular-nums transition-all duration-300 ${(metrics?.ibiArray?.length ?? 0) > 0 ? 'text-violet-400' : 'text-zinc-700'}`}>
            {metrics?.ibiArray?.length ?? '--'}
          </div>
          <div className="text-[10px] text-zinc-600 mt-0.5">beats</div>
        </div>

        {/* Signal quality */}
        <div className="bg-[#04080F] border border-[#1E3A5F]/50 rounded-xl p-3 text-center">
          <div className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Signal</div>
          <div className={`text-3xl font-bold tabular-nums transition-all duration-300 ${
            (metrics?.signalQuality ?? 0) > 0.7 ? 'text-emerald-400' :
            (metrics?.signalQuality ?? 0) > 0.4 ? 'text-orange-400' : 'text-zinc-700'
          }`}>
            {metrics ? `${Math.round(metrics.signalQuality * 100)}%` : '--'}
          </div>
          <div className="text-[10px] text-zinc-600 mt-0.5">quality</div>
        </div>
      </div>

      {/* Quality bar */}
      {metrics && (
        <div className="bg-[#04080F] border border-[#1E3A5F]/50 rounded-xl px-3 py-2">
          <QualityBar quality={metrics.signalQuality} />
        </div>
      )}

      {/* IBI sparkline */}
      {(metrics?.ibiArray?.length ?? 0) >= 3 && (
        <div className="bg-[#04080F] border border-[#1E3A5F]/50 rounded-xl p-3">
          <div className="text-[10px] text-zinc-500 uppercase tracking-wider mb-2">Inter-Beat Intervals (ms)</div>
          <div className="flex items-end gap-1 h-10">
            {metrics!.ibiArray.slice(-20).map((ibi, i) => {
              const minIBI = Math.min(...metrics!.ibiArray);
              const maxIBI = Math.max(...metrics!.ibiArray);
              const range = maxIBI - minIBI || 1;
              const heightPct = ((ibi - minIBI) / range) * 80 + 20;
              return (
                <div
                  key={i}
                  className="flex-1 bg-sky-500/60 rounded-sm transition-all duration-200"
                  style={{ height: `${heightPct}%` }}
                  title={`${Math.round(ibi)}ms`}
                />
              );
            })}
          </div>
        </div>
      )}

      {/* Session complete summary */}
      {sessionState === 'complete' && metrics && (
        <div className="bg-emerald-950/20 border border-emerald-800/30 rounded-xl p-4 space-y-2">
          <div className="text-[12px] font-bold text-emerald-400 mb-2">✓ Session Complete</div>
          <div className="grid grid-cols-2 gap-2 text-[12px]">
            <div className="flex justify-between">
              <span className="text-zinc-500">Heart Rate</span>
              <span className="text-white font-semibold">{metrics.heartRate} BPM</span>
            </div>
            <div className="flex justify-between">
              <span className="text-zinc-500">RMSSD (HRV)</span>
              <span className="text-white font-semibold">{metrics.rmssd} ms</span>
            </div>
            <div className="flex justify-between">
              <span className="text-zinc-500">Duration</span>
              <span className="text-white font-semibold">{elapsed}s</span>
            </div>
            <div className="flex justify-between">
              <span className="text-zinc-500">Signal Quality</span>
              <span className="text-white font-semibold">{Math.round(metrics.signalQuality * 100)}%</span>
            </div>
          </div>
          {saving && <div className="text-[11px] text-zinc-500 animate-pulse">Saving to database…</div>}
          {savedId && <div className="text-[11px] text-emerald-500">✓ Saved (ID: {savedId.slice(0, 8)}…)</div>}
          {eegSessionTag && (
            <div className="text-[11px] text-zinc-500">
              Tagged to EEG session: <span className="text-zinc-300 font-mono">{eegSessionTag}</span>
            </div>
          )}
        </div>
      )}

      {/* Instructions */}
      {sessionState === 'idle' && (
        <div className="bg-[#04080F] border border-[#1E3A5F]/30 rounded-xl p-3 space-y-1.5">
          <div className="text-[11px] font-semibold text-zinc-400 uppercase tracking-wider">How to measure</div>
          {[
            'Place your fingertip gently over the rear camera lens',
            'Cover the flash/torch completely with your fingertip',
            'Hold still for the full 45-second session',
            'Apply gentle, consistent pressure — not too hard',
          ].map((tip, i) => (
            <div key={i} className="flex items-start gap-2 text-[12px] text-zinc-500">
              <span className="text-[#F97316] font-bold mt-0.5">{i + 1}.</span>
              {tip}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
